import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useShopContext } from "../app/ShopGuard";
import type { OrderCreateSummary } from "../api/orders";
import type { Fulfillment } from "../features/checkout/checkoutFields";
import { contactByPhoneHint, messages, orderAcceptedTitle } from "../lib/messages";
import { hapticFeedback, mainButton } from "../lib/telegram";

interface SuccessState {
  order: OrderCreateSummary;
  fulfillment: Fulfillment;
  maskedPhone: string;
}

function asSuccessState(value: unknown): SuccessState | null {
  if (!value || typeof value !== "object" || !("order" in value)) {
    return null;
  }
  return value as SuccessState;
}

/**
 * CheckoutSuccessPage — экран успеха оформления (docs/07-miniapp-ux.md §2.6).
 * Данные заказа приходят через router state от CheckoutPage — минимум,
 * который отдаёт POST /orders (docs/04-api-contract.md §3), без лишнего
 * запроса сразу после создания. Прямой заход на экран (обновление страницы,
 * ссылка без state) отправляет обратно в каталог: смотреть тут нечего.
 */
export function CheckoutSuccessPage() {
  const { shop } = useShopContext();
  const location = useLocation();
  const navigate = useNavigate();
  const state = asSuccessState(location.state);

  useEffect(() => {
    if (!state) {
      navigate("..", { replace: true });
    }
  }, [state, navigate]);

  useEffect(() => {
    if (state) {
      hapticFeedback.notificationOccurred("success");
    }
  }, [state]);

  useEffect(() => {
    mainButton.hide();
  }, []);

  if (!state) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <div aria-hidden="true" className="text-4xl">
        ✓
      </div>
      <h1 className="text-lg font-semibold text-text">{orderAcceptedTitle(state.order.number)}</h1>
      <p className="text-text">{state.order.total_text}</p>
      <p className="text-hint">
        {state.fulfillment === "delivery" ? messages.fulfillmentDelivery : messages.fulfillmentPickup}
      </p>
      <p className="text-sm text-hint">{contactByPhoneHint(state.maskedPhone)}</p>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => navigate("..")}
          className="rounded-card bg-button px-4 py-2 text-button-text"
        >
          {messages.backToCatalog}
        </button>
        {shop.manager_url ? (
          <a href={shop.manager_url} target="_blank" rel="noreferrer" className="text-sm text-link">
            {messages.writeToManager}
          </a>
        ) : null}
      </div>
    </div>
  );
}
